/**
 * Generates a Cloudinary URL based on the provided parameters
 * @param {Object} params - The URL generation parameters
 * @param {string} params.cloudName - Your Cloudinary cloud name
 * @param {string} params.publicId - The public ID of your asset (path to file in Cloudinary)
 * @param {string} params.extension - File extension (e.g., 'jpg', 'png', 'pdf', svg)
 * @param {string} [params.assetType='raw'] - Type of asset ('image', 'video', 'raw')
 * @param {string} [params.deliveryType='upload'] - Delivery type ('upload', 'private', 'authenticated')
 * @param {string} [params.transformations=''] - Cloudinary transformation parameters
 * @param {string} [params.version=''] - Version of the asset
 * @returns {string} The complete Cloudinary URL
 * @throws {Error} If required parameters (cloudName, publicId, extension) are missing
 * @see {@link https://cloudinary.com/documentation/image_transformations#transformation_url_syntax:~:text=language%20you%20require.-,Transformation%20URL%20structure,-The%20default%20Cloudinary|Transformation URL structure}
 * @example
 * const url = cloudinaryUrlGenerator({
 *   cloudName: 'demo',
 *   assetType: 'image',
 *   transformations: 'w_300,h_200,c_crop'
 *   publicId: 'path/to/filename',
 *   extension: 'jpg',
 * });
 */
export const cloudinaryUrlGenerator = ({
  cloudName,
  assetType = 'raw',
  deliveryType = 'upload',
  transformations = '',
  version = '',
  publicId,
  extension
}) => {
  if (!cloudName || !publicId || !extension) {
    throw new Error('Required parameters: cloudName, publicId, extension');
  }

  const baseUrl = 'https://res.cloudinary.com';
  const parts = [
    baseUrl,
    cloudName,
    assetType,
    deliveryType,
    transformations,
    version,
    `${publicId}.${extension}`
  ].filter(Boolean); // Remove empty strings

  return parts.join('/');
};